import { HNPaymentCardStatus } from 'API';
import type { GetCardsPayload } from 'CDK_API';
import { Box, Button, Typography } from '@mui/material';
import { useCardTableUtils } from 'app/pages/cards/cards-table/utils';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { selectCardsDataSlice } from 'app/pages/store/CardDataSlice/cardDataSlice';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { selectMobileActionsLoading } from 'app/pages/store/transactionsSlice';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { ReactComponent as UserAvatarIcon } from 'assets/user-avatar-icon.svg';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MobileCardCarousel from './components/MobileCardCarousel';
import MobileActions from './MobileActions';

// Common styles for message boxes
const messageBoxStyles = {
  mt: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  px: 3,
} as const;

const messageTextStyles = {
  color: 'grey.500',
  textAlign: 'center',
  fontStyle: 'italic',
  fontWeight: 300,
} as const;

const activateButtonStyles = {
  bgcolor: 'primary.main',
  color: 'white',
  px: 4,
  py: 1.5,
  borderRadius: 2,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 600,
  width: '320px',
  '&:hover': {
    bgcolor: 'primary.dark',
  },
} as const;

// Reusable message box component
interface MessageBoxProps {
  message: string;
  showAvatar?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
  messageMaxWidth?: string;
}

const MessageBox: React.FC<MessageBoxProps> = ({
  message,
  showAvatar = false,
  buttonText,
  onButtonClick,
  messageMaxWidth = '280px',
}) => (
  <Box sx={{ ...messageBoxStyles, gap: buttonText ? 3 : 2 }}>
    {showAvatar && <UserAvatarIcon style={{ width: 32, height: 32 }} />}
    <Typography variant="large" sx={{ ...messageTextStyles, maxWidth: messageMaxWidth }}>
      {message}
    </Typography>
    {buttonText && onButtonClick && (
      <Button variant="contained" onClick={onButtonClick} sx={activateButtonStyles}>
        {buttonText}
      </Button>
    )}
  </Box>
);

// Custom hook for card state logic
const useCardStates = (userCardList: GetCardsPayload[] | undefined) => {
  const hasCards = userCardList && userCardList.length > 0;
  const hasUnactivatedCards = userCardList?.some(
    (card) => card.status === HNPaymentCardStatus.ACTIVATION_REQUIRED
  );
  const unactivatedCard = userCardList?.find(
    (card) => card.status === HNPaymentCardStatus.ACTIVATION_REQUIRED
  );

  return { hasCards, hasUnactivatedCards, unactivatedCard };
};

function MobileHomePage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useAppSelector(selectUserProfile);
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const { userCardList, cardsLoading, hasFetchedUserCards } = useAppSelector(selectCardsDataSlice);
  const isTransactionsLoading = useAppSelector(selectMobileActionsLoading);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const { loadUserCardsList } = useCardTableUtils({});

  // Load cards when component mounts (only if not already fetched)
  useEffect(() => {
    if (integration?.financialAccountId && selectedPaidolId && profile?.id && !hasFetchedUserCards) {
      loadUserCardsList(selectedPaidolId, profile.id);
    }
  }, [
    integration?.financialAccountId,
    selectedPaidolId,
    profile?.id,
    hasFetchedUserCards,
    loadUserCardsList,
  ]);

  // Show loading until we've fetched user cards
  const isCardsLoading = cardsLoading || !hasFetchedUserCards;
  const { hasCards } = useCardStates(userCardList);

  // Get the currently active card
  const currentCard = userCardList?.[currentCardIndex];
  const isCurrentCardUnactivated = currentCard?.status === HNPaymentCardStatus.ACTIVATION_REQUIRED;

  const handleActivateCard = () => {
    if (currentCard?.paymentCardId) {
      navigate(`/card/${currentCard.paymentCardId}/activate`);
    }
  };

  const handleCardChange = (index: number) => {
    setCurrentCardIndex(index);
  };

  const renderMobileActions = () => {
    // If no cards at all
    if (!hasCards && hasFetchedUserCards) {
      return <MessageBox message={t('mobileNoCardsAssigned')} showAvatar messageMaxWidth="320px" />;
    }

    // Check the current card's status
    if (isCurrentCardUnactivated) {
      return (
        <MessageBox
          message={t('mobileCardAssignedActivate')}
          buttonText={t('mobileActivateCard')}
          onButtonClick={handleActivateCard}
        />
      );
    }

    // All good - show actions
    return <MobileActions />;
  };

  return (
    <>
      <MobileLoadingOverlay isLoading={isTransactionsLoading || isCardsLoading} />
      <MobilePageContent
        transparentHeight={'24vh'}
        title="Home"
        isHome={true}
        showCompanySwitcher
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          pb: 0,
        }}
      >
        <MobileCardCarousel
          loading={isCardsLoading}
          cards={userCardList || []}
          onCardChange={handleCardChange}
        />
        {renderMobileActions()}
      </MobilePageContent>
    </>
  );
}

export default MobileHomePage;
